import { useEffect, useMemo, useRef, useState } from 'react';
import Spotlight from '@enact/spotlight';
import { NavBar, LogViewer, Slideshow, SignageSlider } from './components/index.js';
import './styles.css';
import { appendLog } from './lib/logStore.js';
import {
  fetchLocalControl,
  normalizeControl,
  saveCachedControl,
  readCachedControl,
  applyCssDisplay,
  applySystemIfAvailable,
  deriveMediaAudio,
  isScreenOff,
} from './lib/control.js';
import { checkAndUpdate } from './lib/updater.js';
import { collectDeviceInfo, reportDeviceInfo } from './lib/device.js';
import {
  DEFAULT_REPORT_URL,
  fetchWebosPlaylist,
  getToken,
  clearToken,
  pair,
  startHeartbeat,
  recordPlay,
  fetchCommands,
} from './lib/api/services/index.js';
import { mapWebosPlaylistToSlideshow } from './lib/playlistMapper.js';

Spotlight.setPointerMode(false);

const USE_LOCAL = false; // prefer network; fallback to local
const LOCAL_DURATION_SEC = 7; // default per-slide duration
const PLAYLIST_POLL_MS = 10_000;
const COMMANDS_POLL_MS = 30_000;
// Define your local playlist explicitly (no scanning, no API)
const DEFAULT_PLAYLIST = [
  {
    id: 'slide-008',
    title: 'slide-008',
    src: 'slide-008.jpg',
    type: 'image/png',
    duration: LOCAL_DURATION_SEC,
  },
  {
    id: 'slide-001',
    title: 'slide-001',
    src: 'slide-001.png',
    type: 'image/png',
    duration: LOCAL_DURATION_SEC,
  },
  { id: 'slide-002', title: 'slide-002', src: 'slide-002.mp4', type: 'video/mp4' },
  {
    id: 'slide-003',
    title: 'slide-003',
    src: 'slide-003.png',
    type: 'image/png',
    duration: LOCAL_DURATION_SEC,
  },
  { id: 'slide-007', title: 'slide-007', src: 'slide-007.mp4', type: 'video/mp4' },
  {
    id: 'slide-004',
    title: 'slide-004',
    src: 'slide-004.png',
    type: 'image/png',
    duration: LOCAL_DURATION_SEC,
  },
  {
    id: 'slide-006',
    title: 'slide-006',
    src: 'slide-006.png',
    type: 'image/png',
    duration: LOCAL_DURATION_SEC,
  },
  { id: 'slide-005', title: 'slide-005', src: 'slide-005.mp4', type: 'video/mp4' },
];

// --- Network + Offline cache helpers ---
const STORAGE_DIR = 'file:///media/internal/didapor'; // LG internal storage
const FILEMAP_KEY = 'didapor:filemap:v1'; // { url -> { path, t, ok } }
const PLAYLIST_CACHE_KEY = 'didapor:lastPlaylist:v1';

function getFileMap() {
  try {
    return JSON.parse(localStorage.getItem(FILEMAP_KEY) || '{}');
  } catch {
    return {};
  }
}
function setFileMap(map) {
  try {
    localStorage.setItem(FILEMAP_KEY, JSON.stringify(map || {}));
  } catch {}
}
function savePlaylistCache(items) {
  try {
    localStorage.setItem(PLAYLIST_CACHE_KEY, JSON.stringify({ t: Date.now(), items }));
  } catch {}
}
function readPlaylistCache() {
  try {
    const v = JSON.parse(localStorage.getItem(PLAYLIST_CACHE_KEY) || 'null');
    return v?.items || [];
  } catch {
    return [];
  }
}

function isOnline() {
  // navigator.onLine is flaky; attempt to trust it lightly
  try {
    return navigator.onLine !== false;
  } catch {
    return true;
  }
}

function sanitizeFilename(url) {
  try {
    const u = new URL(url);
    const base = (u.pathname.split('/').pop() || '').split('?')[0].split('#')[0] || 'asset';
    return base.replace(/[^a-zA-Z0-9._-]/g, '_');
  } catch {
    return 'asset';
  }
}

function webOSDownload(url, targetDir, targetName) {
  return new Promise((resolve) => {
    const svc = window?.webOS?.service;
    if (!svc || !url) return resolve('');
    try {
      const params = { target: url, targetDir, targetName, subscribe: true };
      let done = false;
      const complete = (p) => {
        if (done) return;
        done = true;
        const path = p?.path || p?.filePath || p?.savePath || `${targetDir}/${targetName}`;
        resolve(path);
      };
      svc.request('luna://com.webos.service.downloadmanager', {
        method: 'download',
        parameters: params,
        onSuccess: (res) => {
          try {
            // Accept various completion signals
            const status = String(res?.status || res?.state || res?.event || '').toLowerCase();
            if (
              res?.completed === true ||
              status === 'completed' ||
              status === 'complete' ||
              res?.finished === true
            ) {
              complete(res);
            } else if (
              !res?.subscribe &&
              res?.returnValue === true &&
              (res?.filePath || res?.savePath)
            ) {
              complete(res);
            }
          } catch {}
        },
        onFailure: () => resolve(''),
      });
    } catch {
      resolve('');
    }
  });
}

async function ensureLocalAsset(url) {
  // Try to download to internal storage via webOS; else no local path
  const name = sanitizeFilename(url);
  // primary: subfolder
  let localPath = await webOSDownload(url, STORAGE_DIR, name);
  if (!localPath) {
    // fallback: root internal directory (commonly present)
    localPath = await webOSDownload(url, 'file:///media/internal', name);
  }
  return localPath || '';
}

function applyLocalMapping(items, fileMap) {
  return (items || []).map((it) => {
    const m = fileMap?.[it.src];
    if (m?.ok && m?.path) return { ...it, src: m.path };
    return { ...it };
  });
}

export default function App() {
  const [view, setView] = useState('slideshow'); // 'slideshow' | 'logs' | 'scap-slider'
  const [playlist, setPlaylist] = useState(USE_LOCAL ? DEFAULT_PLAYLIST : []);
  const [loading, setLoading] = useState(!USE_LOCAL);
  const [loadError, setLoadError] = useState('');
  const [token, setTokenState] = useState(() => getToken());
  const [pairingCode, setPairingCode] = useState('');
  const [pairingError, setPairingError] = useState('');
  const [proxyOk, setProxyOk] = useState(null); // null=not checked, true/false=result of /api-proxy-status
  const [startIndex, setStartIndex] = useState(0);
  const [slideshowKey, setSlideshowKey] = useState('slideshow:init');
  const [audioCtl, setAudioCtl] = useState({ startWithSound: true, initialVolume: 1 });
  const updateUrlsRef = useRef([]);
  const deviceReportUrlRef = useRef('');
  const controlSigRef = useRef('');
  const fileMapRef = useRef(getFileMap());
  const playlistMetaRef = useRef({ playlistId: 0, version: 0 });
  const pendingPlaylistRef = useRef(null);
  const appliedCommandsRef = useRef(new Set());
  const downloadsStopRef = useRef(false);
  const mainRef = useRef(null);
  const lastViewRef = useRef('home');
  const navStartRef = useRef(0);
  const autoStartedRef = useRef(false);
  const retryRef = useRef(null);

  const startDownloads = async (items) => {
    const fileMap = fileMapRef.current || getFileMap();
    fileMapRef.current = fileMap;
    for (const it of items || []) {
      if (downloadsStopRef.current) return;
      const url = it?.src;
      if (!url) continue;
      if (fileMap[url]?.ok) continue; // already mirrored
      try {
        const path = await ensureLocalAsset(url);
        if (downloadsStopRef.current) return;
        if (path) {
          fileMap[url] = { path, ok: true, t: Date.now() };
          setFileMap(fileMap);
          // hot-swap src to local path and update cache
          setPlaylist((prev) => {
            const next = prev.map((p) => (p.src === url ? { ...p, src: path } : p));
            savePlaylistCache(next);
            return next;
          });
          appendLog({ type: 'info', detail: { event: 'mirror-complete', url, path } });
        }
      } catch (e) {
        appendLog({
          type: 'error',
          detail: { event: 'mirror-failed', url, message: String(e?.message || e) },
        });
      }
    }
  };

  useEffect(() => {
    Spotlight.resume();
    downloadsStopRef.current = false;
    return () => {
      downloadsStopRef.current = true;
    };
  }, []);

  // Load and apply local control.json (+polling)
  useEffect(() => {
    let cancelled = false;
    let lastSig = '';
    const setScreenCss = (off) => {
      try {
        document.documentElement.style.setProperty('--ctl-screen-off', off ? '1' : '0');
      } catch {}
    };
    const apply = (cfg) => {
      if (!cfg) return;
      applyCssDisplay(cfg);
      applySystemIfAvailable(cfg);
      const a = deriveMediaAudio(cfg);
      if (!cancelled) setAudioCtl(a);
      try {
        updateUrlsRef.current = (cfg?.update?.manifestUrls || []).filter(Boolean);
      } catch {}
      try {
        deviceReportUrlRef.current = String(cfg?.device?.reportUrl || '');
      } catch {}
      setScreenCss(isScreenOff(cfg));
      appendLog({
        type: 'info',
        detail: { event: 'control-applied', audio: a, display: cfg.display, power: cfg.power },
      });
    };
    (async () => {
      try {
        const raw = await fetchLocalControl();
        const cfg = normalizeControl(raw || readCachedControl() || {});
        if (cancelled) return;
        saveCachedControl(cfg);
        lastSig = JSON.stringify(cfg);
        controlSigRef.current = lastSig;
        apply(cfg);
      } catch {
        const cached = readCachedControl();
        if (cached) {
          controlSigRef.current = JSON.stringify(cached);
          apply(cached);
        }
      }
    })();
    // Poll every 30s
    const id = setInterval(async () => {
      try {
        const raw = await fetchLocalControl();
        if (!raw) return;
        const cfg = normalizeControl(raw);
        const sig = JSON.stringify(cfg);
        if (sig !== lastSig) {
          saveCachedControl(cfg);
          lastSig = sig;
          controlSigRef.current = sig;
          apply(cfg);
        }
      } catch {}
    }, 30000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);

  // Device info report once at boot (after control loaded)
  useEffect(() => {
    let stop = false;
    const send = async () => {
      try {
        const info = await collectDeviceInfo();
        const url = deviceReportUrlRef.current || DEFAULT_REPORT_URL;
        if (!url) return;
        await reportDeviceInfo(info, url, { app: { id: 'com.lg.app.signage' } });
      } catch {}
    };
    const t = setTimeout(() => {
      if (!stop) send();
    }, 4000);
    return () => {
      stop = true;
      clearTimeout(t);
    };
  }, []);

  const refreshControlNow = async () => {
    try {
      const raw = await fetchLocalControl();
      if (!raw) {
        appendLog({ type: 'warn', detail: { event: 'control-refresh', status: 'no-response' } });
        return;
      }
      const cfg = normalizeControl(raw);
      const sig = JSON.stringify(cfg);
      if (sig !== controlSigRef.current) {
        saveCachedControl(cfg);
        controlSigRef.current = sig;
      }
      applyCssDisplay(cfg);
      applySystemIfAvailable(cfg);
      const a = deriveMediaAudio(cfg);
      setAudioCtl(a);
      appendLog({ type: 'info', detail: { event: 'control-refresh', status: 'applied' } });
    } catch (e) {
      appendLog({
        type: 'error',
        detail: { event: 'control-refresh', error: String(e?.message || e) },
      });
    }
  };

  // Auto-update: check at boot and every 6 hours using configured update URLs (fallback /update.json)
  useEffect(() => {
    let stop = false;
    const run = async () => {
      try {
        await checkAndUpdate({ updateUrls: updateUrlsRef.current });
      } catch {}
      if (!stop) setTimeout(run, 6 * 60 * 60 * 1000);
    };
    run();
    return () => {
      stop = true;
    };
  }, []);

  // Heartbeat: setiap 5 detik (API 001)
  useEffect(() => {
    if (!token) return;
    const stop = startHeartbeat(token, {
      intervalMs: 5000,
      onUnauthorized: () => {
        try {
          clearToken();
        } catch {}
        setTokenState('');
        setPairingError('Session expired. Please pair again.');
      },
    });
    return () => {
      try {
        stop?.();
      } catch {}
    };
  }, [token]);

  // Load network playlist, mirror assets to internal storage, and keep offline cache
  useEffect(() => {
    if (USE_LOCAL) return; // keep local mode untouched
    if (!token) return;

    let cancelled = false;
    const fileMap = getFileMap();
    fileMapRef.current = fileMap;

    const useItems = (items, note) => {
      const applied = applyLocalMapping(items, fileMap);
      setPlaylist(applied);
      savePlaylistCache(applied);
      if (note)
        appendLog({ type: 'info', detail: { event: 'playlist', note, count: applied.length } });
    };

    (async () => {
      setLoading(true);
      setLoadError('');
      try {
        const res = await fetchWebosPlaylist(token, { timeout: 8000 });
        if (cancelled) return;
        if (!res.ok) {
          if (res.status === 401 || res.status === 403) {
            try {
              clearToken();
            } catch {}
            setTokenState('');
            setPairingError('Invalid token. Please pair again.');
            throw new Error('unauthorized');
          }
          throw new Error(res.json?.message || `HTTP ${res.status}`);
        }

        const mapped = mapWebosPlaylistToSlideshow(res.json);
        playlistMetaRef.current = {
          playlistId: mapped.meta.playlistId,
          version: mapped.meta.version,
        };
        setStartIndex(mapped.startIndex);
        setSlideshowKey(`slideshow:${mapped.meta.playlistId}:${mapped.meta.version}`);
        useItems(mapped.items, 'remote');
        // mirror in background
        void startDownloads(mapped.items);
      } catch (e) {
        // Fallback to cached playlist, then to local default
        const cached = readPlaylistCache();
        if (cached?.length) {
          useItems(cached, 'cache');
        } else {
          setPlaylist(DEFAULT_PLAYLIST);
          appendLog({
            type: 'warn',
            detail: { event: 'fallback-local', reason: String(e?.message || e) },
          });
        }
        setLoadError(String(e?.message || 'network error'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [token]);

  // Poll playlist: simpan pending jika ada perubahan, apply di batas aman (awal cycle)
  useEffect(() => {
    if (USE_LOCAL) return;
    if (!token) return;

    let stop = false;
    const poll = async () => {
      try {
        const res = await fetchWebosPlaylist(token, { timeout: 8000 });
        if (!res.ok) {
          if (res.status === 401 || res.status === 403) {
            try {
              clearToken();
            } catch {}
            setTokenState('');
            setPairingError('Invalid token. Please pair again.');
          }
          return;
        }
        const mapped = mapWebosPlaylistToSlideshow(res.json);
        const cur = playlistMetaRef.current;
        if (
          mapped.meta.playlistId &&
          (mapped.meta.version !== cur.version || mapped.meta.playlistId !== cur.playlistId)
        ) {
          pendingPlaylistRef.current = mapped;
          appendLog({
            type: 'info',
            detail: { event: 'playlist-update-available', from: cur, to: mapped.meta },
          });
        }
      } catch {}
    };

    const id = setInterval(() => {
      if (!stop) void poll();
    }, PLAYLIST_POLL_MS);
    return () => {
      stop = true;
      clearInterval(id);
    };
  }, [token]);

  // Poll commands: apply yang baru saja muncul
  useEffect(() => {
    if (!token) return;
    let stop = false;

    const applyBrightness = (pct) => {
      const n = Number(pct);
      if (!Number.isFinite(n)) return;
      const clamped = Math.max(0, Math.min(100, n));
      const b = Math.max(0, Math.min(200, clamped)) / 100;
      const dim = Math.max(0, Math.min(1, (100 - clamped) / 100));
      try {
        const root = document.documentElement;
        root.style.setProperty('--ctl-brightness', String(b));
        root.style.setProperty('--ctl-dim', String(dim));
      } catch {}
    };

    const tick = async () => {
      try {
        const list = await fetchCommands(token);
        for (const cmd of list || []) {
          const id = cmd?.publicId || cmd?.id;
          if (!id) continue;
          if (appliedCommandsRef.current.has(id)) continue;
          appliedCommandsRef.current.add(id);

          const type = String(cmd?.type || '').toLowerCase();
          const args = cmd?.args || {};
          if (type === 'set_brightness') {
            const v = args?.brightness ?? args?.value ?? args?.level;
            applyBrightness(v);
          }
          appendLog({ type: 'info', detail: { event: 'command-applied', type, id, args } });
        }
      } catch (e) {
        if (String(e?.status) === '401' || String(e?.status) === '403') {
          try {
            clearToken();
          } catch {}
          setTokenState('');
          setPairingError('Invalid token. Please pair again.');
        }
      }
    };

    tick();
    const id = setInterval(() => {
      if (!stop) void tick();
    }, COMMANDS_POLL_MS);
    return () => {
      stop = true;
      clearInterval(id);
    };
  }, [token]);

  // (removed duplicate fetch effect; single remote fetch above drives playlist)

  const KIOSK = true;
  const header = useMemo(() => {
    if (KIOSK) return null;
    switch (view) {
      case 'logs':
        return <NavBar title="Logs" onBack={() => setView('slideshow')} />;
      case 'scap-slider':
        return <NavBar title="Slider storage (SCAP)" onBack={() => setView('slideshow')} />;
      default:
        return (
          <NavBar
            title="Slideshow"
            right={
              <>
                <button onClick={() => setView('scap-slider')}>Storage (SCAP)</button>
                <button onClick={() => setView('logs')}>Logs</button>
              </>
            }
          />
        );
    }
  }, [view]);

  // Cek proxy saat di halaman pairing (Play Via URL harus lewat proxy agar tidak CORS)
  // Di origin non-http(s) (file://, app terpasang webOS) skip fetch agar tidak request file:///...
  useEffect(() => {
    if (token || USE_LOCAL) return;
    const isHttp =
      typeof window !== 'undefined' &&
      (window.location?.protocol === 'http:' || window.location?.protocol === 'https:');
    if (!isHttp) {
      setProxyOk(false);
      return;
    }
    let cancelled = false;
    fetch('/api-proxy-status', { cache: 'no-store' })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (!cancelled) setProxyOk(Boolean(data?.proxy));
      })
      .catch(() => {
        if (!cancelled) setProxyOk(false);
      });
    return () => { cancelled = true; };
  }, [token]);

  const isFileOrigin =
    typeof window !== 'undefined' && String(window.location?.protocol || '') === 'file:';
  const isHttpOrigin =
    typeof window !== 'undefined' &&
    (window.location?.protocol === 'http:' || window.location?.protocol === 'https:');

  const content = useMemo(() => {
    if (!token && !USE_LOCAL) {
      const onSubmit = async (e) => {
        try {
          e?.preventDefault?.();
        } catch {}
        setPairingError('');
        try {
          const res = await pair(pairingCode);
          setTokenState(res.token);
          setPairingCode('');
          appendLog({ type: 'info', detail: { event: 'paired', screen: res.screen } });
        } catch (err) {
          const msg = String(err?.message || 'Pairing failed');
          const isNetworkError =
            msg.includes('fetch') ||
            msg === 'Failed to fetch' ||
            err?.name === 'TypeError' ||
            err?.code === 'ERR_NETWORK';
          const hint =
            isNetworkError && !isHttpOrigin
              ? 'Backend harus mengaktifkan CORS untuk origin aplikasi ini. Atau buka app lewat Play Via URL: https://player.getface.id atau http://IP-komputer:4174.'
              : isNetworkError && proxyOk === false
                ? 'Buka app lewat Play Via URL (https://player.getface.id atau http://IP-komputer:4174) atau pastikan backend mengaktifkan CORS.'
                : msg;
          setPairingError(hint);
        }
      };
      return (
        <div
          className="page pairingPage"
          ref={mainRef}
          data-page="pairing"
          data-spotlight-container
          data-spotlight-id="page-pairing"
        >
          <div className="pairingCard">
            <h2 className="pairingTitle">Pairing</h2>
            <div className="pairingSubtitle">Masukkan pairing code dari dashboard.</div>
            {!isHttpOrigin ? (
              <div className="pairingHint" style={{ fontSize: 13, color: '#8b92a0', marginBottom: 12 }}>
                App terpasang (.ipk): pastikan backend mengaktifkan CORS untuk origin aplikasi. Lihat docs/CORS-Laravel.md.
              </div>
            ) : proxyOk === false ? (
              <div className="pairingHint" style={{ fontSize: 13, color: '#8b92a0', marginBottom: 12 }}>
                Buka dari <strong>http://IP-komputer:4174</strong> (npm run play-via-url) atau pastikan proxy/backend siap.
              </div>
            ) : null}
            <form onSubmit={onSubmit} className="pairingForm">
              <input
                className="pairingInput"
                value={pairingCode}
                onChange={(e) => setPairingCode(e.target.value)}
                placeholder="PAIRING_CODE"
                autoCapitalize="characters"
                autoCorrect="off"
                spellCheck={false}
              />
              <button type="submit" className="pairingButton">
                Pair
              </button>
            </form>
            {pairingError ? <div className="pairingError">{pairingError}</div> : null}
          </div>
        </div>
      );
    }

    if (view === 'logs') {
      return (
        <div
          className="page"
          ref={mainRef}
          data-page="logs"
          data-spotlight-container
          data-spotlight-id="page-logs"
        >
          <LogViewer />
        </div>
      );
    }

    if (view === 'scap-slider') {
      return (
        <div
          className="page"
          ref={mainRef}
          data-page="scap-slider"
          data-spotlight-container
          data-spotlight-id="page-scap-slider"
        >
          <SignageSlider storagePath="file://internal/content" durationMs={7000} />
        </div>
      );
    }

    return (
      <div
        className="page"
        ref={mainRef}
        data-page="slideshow"
        data-spotlight-container
        data-spotlight-id="page-slideshow"
      >
        {loading ? (
          <div
            style={{
              position: 'absolute',
              left: 0,
              right: 0,
              bottom: 0,
              padding: '10px 16px',
              zIndex: 3,
            }}
          >
            <span style={{ background: 'rgba(0,0,0,.4)', padding: '6px 10px', borderRadius: 6 }}>
              Loading playlist…
            </span>
          </div>
        ) : playlist && playlist.length ? (
          <Slideshow
            key={slideshowKey}
            items={playlist}
            startIndex={startIndex}
            onItemStart={(idx, item) => {
              // Apply pending playlist at the safest boundary: start of cycle (idx 0)
              if (idx === 0 && pendingPlaylistRef.current) {
                const p = pendingPlaylistRef.current;
                pendingPlaylistRef.current = null;
                playlistMetaRef.current = {
                  playlistId: p.meta.playlistId,
                  version: p.meta.version,
                };
                const fileMap = fileMapRef.current || getFileMap();
                fileMapRef.current = fileMap;
                const applied = applyLocalMapping(p.items, fileMap);
                setStartIndex(p.startIndex || 0);
                setSlideshowKey(`slideshow:${p.meta.playlistId}:${p.meta.version}`);
                setPlaylist(applied);
                savePlaylistCache(applied);
                appendLog({
                  type: 'info',
                  detail: { event: 'playlist-updated', meta: p.meta, count: applied.length },
                });
                // mirror new items in background
                void startDownloads(p.items);
              }

              // Proof-of-play: record when slide starts (dedupe handled in service)
              const w = item?._webos;
              if (token && w?.playlistId && w?.itemId) {
                recordPlay(token, {
                  playlist_id: w.playlistId,
                  item_id: w.itemId,
                  content_id: w.contentId || 0,
                  started_at: new Date().toISOString(),
                });
              }
            }}
            onExit={() => setView('slideshow')}
            kiosk
            startWithSound={audioCtl.startWithSound}
            initialVolume={audioCtl.initialVolume}
          />
        ) : (
          <div style={{ padding: 12, opacity: 0.8 }}>
            No items. {loadError ? `(${loadError})` : ''}
          </div>
        )}
      </div>
    );
  }, [
    isHttpOrigin,
    isFileOrigin,
    view,
    playlist,
    loading,
    loadError,
    token,
    proxyOk,
    pairingCode,
    pairingError,
    slideshowKey,
    startIndex,
    audioCtl,
  ]);

  // Perf: measure transition time when view changes
  useEffect(() => {
    const from = lastViewRef.current;
    const to = view;
    navStartRef.current = performance.now();
    // After next paint, measure duration
    const id = requestAnimationFrame(() => {
      const dur = performance.now() - navStartRef.current;
      appendLog({
        type: 'perf',
        detail: { event: 'transition', from, to, durationMs: Math.round(dur) },
      });
    });
    lastViewRef.current = to;
    return () => cancelAnimationFrame(id);
  }, [view]);

  // Spotlight: focus the current page container
  useEffect(() => {
    const pageId = view === 'logs' ? 'page-logs' : 'slideshow-stage';
    try {
      Spotlight.focus(pageId);
    } catch {}
  }, [view]);

  // Global D-pad/back handling
  useEffect(() => {
    const onKey = (e) => {
      // Map webOS remotes (Back/Escape) to back navigation
      if (e.key === 'Escape' || e.key === 'Backspace') {
        if (view === 'logs') {
          setView('slideshow');
          e.preventDefault();
        }
      }
      if (
        e.key === 'c' ||
        e.key === 'C' ||
        e.code === 'KeyC' ||
        e.key === 'F5' ||
        e.key === 'r' ||
        e.key === 'R' ||
        e.code === 'KeyR'
      ) {
        e.preventDefault();
        refreshControlNow();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [view]);

  return (
    <div className="app" data-spotlight-container data-spotlight-id="app-root">
      {/* keep a stable title for tests and accessibility */}
      <span style={{ position: 'absolute', left: -9999, top: -9999 }}>webOS Player</span>
      {header}
      {content}
    </div>
  );
}
